import { useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { useAuthStore } from "@/stores/auth";
import { useCreateTeamMutation, useSetActiveTeamMutation } from "@/api/teams";
import { Layout } from "@/components/layout/Layout";

export function CreateTeamPage() {
  const router = useRouter();
  const setActiveTeam = useAuthStore((s) => s.setActiveTeam);

  const createTeam = useCreateTeamMutation();
  const setActiveMutation = useSetActiveTeamMutation();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState<string | null>(null);

  function generateSlug(input: string) {
    return input
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }

  function handleNameChange(value: string) {
    setName(value);
    setSlug(generateSlug(value));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!name.trim() || !slug.trim()) {
      setError("Name and slug are required");
      return;
    }

    try {
      const team = await createTeam.mutateAsync({
        name: name.trim(),
        slug: slug.trim(),
      });
      // Switch to the new team
      setActiveTeam(team.id);
      await setActiveMutation.mutateAsync(team.id);
      void router.navigate({ to: `/teams/${team.id}/settings` });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to create team");
    }
  }

  return (
    <Layout>
      <div className="mx-auto max-w-md space-y-6 p-6">
        <h1 className="text-2xl font-semibold text-ink">
          Create Team
        </h1>

        {error && (
          <div className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-2">
              Team Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="My Team"
              className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-ink-2">
              Slug
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="my-team"
              className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
            />
            <p className="mt-1 text-xs text-ink-3">
              Lowercase letters, numbers, and hyphens only
            </p>
          </div>

          <button
            type="submit"
            disabled={createTeam.isPending}
            className="w-full rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
          >
            {createTeam.isPending ? "Creating..." : "Create Team"}
          </button>
        </form>
      </div>
    </Layout>
  );
}
